import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import api from '../services/api';
import { 
  Rocket, Zap, Crown, TrendingUp, Eye, Users, Star,
  Check, ArrowRight, Shield, Clock, ChevronRight, Sparkles,
  Target, Award, Mail, Loader2
} from 'lucide-react';

const BoostListingPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  
  const [listing, setListing] = useState(null);
  const [packages, setPackages] = useState({});
  const [selectedPackage, setSelectedPackage] = useState('boost_month');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const isFromOnboarding = searchParams.get('from') === 'onboarding';

  useEffect(() => {
    loadData();
  }, [listingId]);

  const loadData = async () => {
    try {
      // Load listing
      const listingRes = await api.get(`/listings/${listingId}`);
      setListing(listingRes.data);
      
      // Load boost packages
      const packagesRes = await api.get('/boost/packages');
      setPackages(packagesRes.data.packages || {});
    } catch (error) {
      toast({ title: 'Fout bij laden', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleBoost = async () => {
    if (!selectedPackage) return;
    
    setProcessing(true);
    try {
      const response = await api.post('/boost/checkout', {
        listing_id: listingId,
        package_id: selectedPackage,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({ 
        title: 'Fout', 
        description: error.response?.data?.detail || 'Er ging iets mis',
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleSkip = () => {
    navigate('/verhuurder/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" />
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  const stats = [
    { icon: Eye, value: '5x', label: language === 'nl' ? 'meer views' : 'more views' },
    { icon: Users, value: '3x', label: language === 'nl' ? 'meer reacties' : 'more responses' },
    { icon: Clock, value: '70%', label: language === 'nl' ? 'sneller verhuurd' : 'faster rented' },
  ];

  const packageOrder = ['boost_week', 'boost_month', 'boost_premium'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137]" data-testid="boost-page">
      <Toaster />
      
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-[10%] w-96 h-96 bg-[#2E7D32]/30 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-20 right-[10%] w-[500px] h-[500px] bg-amber-500/20 rounded-full blur-[150px] animate-pulse" style={{animationDelay: '1s'}} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8 md:py-16">
        {/* Success Header */}
        {isFromOnboarding && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-6 py-3 rounded-full text-lg font-bold mb-4 animate-bounce">
              <Check className="w-6 h-6" />
              {language === 'nl' ? 'Woning geplaatst!' : 'Property listed!'}
            </div>
            <p className="text-white/70 text-lg">
              {language === 'nl' 
                ? 'Je advertentie wordt nu beoordeeld en is binnen 24 uur zichtbaar.'
                : 'Your listing is being reviewed and will be visible within 24 hours.'}
            </p>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Sparkles className="w-4 h-4" />
                {language === 'nl' ? 'EXCLUSIEF VOOR VERHUURDERS' : 'EXCLUSIVE FOR LANDLORDS'}
              </div>
              
              <h1 className="text-3xl md:text-5xl font-black mb-4">
                <Rocket className="w-10 h-10 md:w-12 md:h-12 inline mr-3 animate-bounce" />
                {language === 'nl' ? 'Boost je woning!' : 'Boost your listing!'}
              </h1>
              
              <p className="text-xl md:text-2xl font-medium opacity-90 max-w-2xl mx-auto">
                {language === 'nl' 
                  ? 'Wordt als eerste gezien door huurders en verhuur sneller'
                  : 'Be seen first by tenants and rent faster'}
              </p>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="bg-[#0D2137] py-6">
            <div className="flex justify-center gap-8 md:gap-16">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="flex items-center justify-center gap-2 text-amber-400 mb-1">
                    <stat.icon className="w-5 h-5" />
                    <span className="text-2xl md:text-3xl font-black">{stat.value}</span>
                  </div>
                  <p className="text-white/60 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Listing Preview */}
          {listing && (
            <div className="p-6 bg-[#F7F7F7] border-b">
              <div className="flex items-center gap-4 max-w-xl mx-auto">
                <img 
                  src={listing.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=200'} 
                  alt={listing.title}
                  className="w-20 h-20 rounded-xl object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-[#2A2A2A] truncate">{listing.title}</h3>
                  <p className="text-[#9E9E9E] text-sm">{listing.neighborhood}, {listing.city}</p>
                  <p className="text-[#2E7D32] font-bold">€{listing.price}/mnd</p>
                </div>
              </div>
            </div>
          )}

          {/* Package Selection */}
          <div className="p-6 md:p-8">
            <h2 className="text-xl font-bold text-center text-[#2A2A2A] mb-6">
              {language === 'nl' ? 'Kies je boost pakket' : 'Choose your boost package'}
            </h2>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {packageOrder.map((pkgId) => {
                const pkg = packages[pkgId];
                if (!pkg) return null;
                
                const isPopular = pkgId === 'boost_month';
                const isPremium = pkgId === 'boost_premium';
                const isSelected = selectedPackage === pkgId;
                
                return (
                  <button
                    key={pkgId}
                    onClick={() => setSelectedPackage(pkgId)}
                    className={`relative p-6 rounded-2xl border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-amber-500 bg-amber-50 scale-[1.02] shadow-lg' 
                        : 'border-[#E5E5E5] bg-white hover:border-amber-300 hover:shadow-md'
                    }`}
                  >
                    {/* Popular Badge */}
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        {language === 'nl' ? 'MEEST GEKOZEN' : 'MOST POPULAR'}
                      </div>
                    )}
                    
                    {/* Premium Badge */}
                    {isPremium && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        PREMIUM
                      </div>
                    )}
                    
                    {/* Package Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      isPremium ? 'bg-purple-100' : isPopular ? 'bg-amber-100' : 'bg-gray-100'
                    }`}>
                      {isPremium ? <Crown className="w-6 h-6 text-purple-600" /> :
                       isPopular ? <Zap className="w-6 h-6 text-amber-600" /> :
                       <Rocket className="w-6 h-6 text-gray-600" />}
                    </div>
                    
                    {/* Package Name & Duration */}
                    <h3 className="font-bold text-[#2A2A2A] text-lg mb-1">
                      {pkgId === 'boost_week' && (language === 'nl' ? '1 Week Boost' : '1 Week Boost')}
                      {pkgId === 'boost_month' && (language === 'nl' ? '1 Maand Boost' : '1 Month Boost')}
                      {pkgId === 'boost_premium' && (language === 'nl' ? 'Premium Boost' : 'Premium Boost')}
                    </h3>
                    
                    <p className="text-sm text-[#9E9E9E] mb-4">{pkg.description}</p>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-4">
                      {pkg.features?.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#2A2A2A]">
                          <Check className={`w-4 h-4 ${isPremium ? 'text-purple-500' : 'text-amber-500'}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Price */}
                    <div className="flex items-baseline gap-1 pt-4 border-t border-[#E5E5E5]">
                      <span className="text-3xl font-black text-[#2A2A2A]">€{pkg.amount}</span>
                      <span className="text-[#9E9E9E] text-sm">
                        {language === 'nl' ? 'eenmalig' : 'one-time'}
                      </span>
                    </div>
                    
                    {/* Selected indicator */}
                    {isSelected && (
                      <div className="absolute top-4 right-4 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <Button
                onClick={handleBoost}
                disabled={processing || !selectedPackage}
                className="flex-1 h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-bold text-lg shadow-lg"
                data-testid="boost-cta-btn"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Rocket className="w-5 h-5 mr-2" />
                    {language === 'nl' ? 'Nu boosten' : 'Boost now'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleSkip}
                variant="outline"
                className="h-14 px-8 rounded-xl border-[#E5E5E5] text-[#9E9E9E] hover:text-[#2A2A2A]"
              >
                {language === 'nl' ? 'Overslaan' : 'Skip'}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-[#9E9E9E]">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#2E7D32]" />
                {language === 'nl' ? 'Veilig betalen' : 'Secure payment'}
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#2E7D32]" />
                {language === 'nl' ? 'Direct actief' : 'Instant activation'}
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#2E7D32]" />
                {language === 'nl' ? 'Geld-terug garantie' : 'Money-back guarantee'}
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-[#F7F7F7] p-6 md:p-8">
            <div className="max-w-xl mx-auto text-center">
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-[#2A2A2A] italic mb-4">
                "{language === 'nl' 
                  ? 'Met de boost had ik binnen 3 dagen al 15 reacties. Normaal duurde dat weken!'
                  : 'With the boost I had 15 responses within 3 days. Normally that took weeks!'}"
              </p>
              <div className="flex items-center justify-center gap-3">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" 
                  alt="User"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="text-left">
                  <p className="font-semibold text-[#2A2A2A]">Pieter V.</p>
                  <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Verhuurder in Amsterdam' : 'Landlord in Amsterdam'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoostListingPage;
